/**
 * @render react
 * @name ShortAuthorBio
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * ShortAuthorBio component
 * @param {string} authorBio - Author bio text
 * @param {string} authorLink - Link to author's bio
 * @param {Object} authorImage - Author image
 * @param {string} authorName - Author name
 * @param {string} authorRole - Author role
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */
const ShortAuthorBio = (
  {
    authorBio,
    authorLink,
    authorImage,
    authorName,
    authorRole,
    singleImage,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => {
  if (
    !authorBio
    || !authorLink
    || !authorImage
    || !authorImage.src
  ) return null;

  const image = (
    <Image
      src={authorImage.src}
      alt={authorImage.alt}
      height={authorImage.height}
      width={authorImage.width}
      size="author"
    />
  );

  // This is only for where we need to have two images for mobile and desktop.
  const mobileImageClass = singleImage ? `${cssPrefix}__shortauthorbio-image` : `${cssPrefix}__shortauthorbio-image--mobile`;

  return (
    <div
      className={classNames({
        [`${cssPrefix}__shortauthorbio`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {(singleImage !== true) && (
        <div className={`${cssPrefix}__shortauthorbio-image--desktop`}>
          {image}
        </div>
      )}
      <div>
        <div className={`${cssPrefix}__shortauthorbio-author`}>
          <div className={`${mobileImageClass}`}>
            {image}
          </div>
          <div className={`${cssPrefix}__shortauthorbio-authorinfo`}>
            <div className={`${cssPrefix}__shortauthorbio-authorname`}>
              <Link href={authorLink}>{authorName}</Link>
            </div>
            <div className={`${cssPrefix}__shortauthorbio-authorrole`}>{authorRole}</div>
          </div>
        </div>
        <div className={`${cssPrefix}__shortauthorbio-bio`}>
          {authorBio}
        </div>
      </div>
    </div>
  );
};

/**
 * Valid props
 */
ShortAuthorBio.propTypes = {
  authorBio: PropTypes.oneOfType([
    PropTypes.shape({
      html: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  authorLink: PropTypes.string,
  authorImage: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  authorName: PropTypes.string,
  authorRole: PropTypes.string,
  singleImage: PropTypes.bool,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
};

/**
 * Default props
 */
ShortAuthorBio.defaultProps = {
  authorBio: '',
  authorLink: '',
  authorImage: {},
  authorName: null,
  authorRole: null,
  singleImage: false,
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(ShortAuthorBio);
